class CSSPathStep {
    constructor(
        private value: string,
        public optimized: boolean,
    ) {}

    toString() {
        return this.value;
    }
}

export class CSSPathService {
    cssPath(node: Element, optimized = false): string {
        try {
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return '';
            }
            const steps = [];
            let contextNode = node;

            while (contextNode) {
                const step = this.createStep(contextNode, Boolean(optimized), contextNode === node);

                if (!step) {
                    break;
                } // Error - bail out early.
                steps.push(step);
                if (step?.optimized) {
                    break;
                }
                contextNode = contextNode.parentNode as Element;
            }

            steps.reverse();

            return steps.join(' > ');
        } catch (ex) {
            console.error(ex);

            return '';
        }
    }

    private createStep(
        node: Element,
        optimized: boolean,
        isTargetNode: boolean,
    ): CSSPathStep | null {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }

        const id = node.getAttribute('id');

        if (optimized) {
            if (id) {
                return new CSSPathStep(this.idSelector(id), true);
            }
            const nodeNameLower = node.nodeName.toLowerCase();

            if (nodeNameLower === 'body' || nodeNameLower === 'head' || nodeNameLower === 'html') {
                return new CSSPathStep(node.nodeName.toLowerCase(), true);
            }
        }

        const nodeName = node.nodeName.toLowerCase();

        if (id) {
            return new CSSPathStep(nodeName.toLowerCase() + this.idSelector(id), true);
        }

        const parent = node.parentNode;

        if (!parent || parent.nodeType === Node.DOCUMENT_NODE) {
            return new CSSPathStep(nodeName.toLowerCase(), true);
        }

        const prefixedOwnClassNamesArray = this.prefixedElementClassNames(node);
        const siblings = parent.children;
        let needsClassNames = false;
        let needsNthChild = false;
        let ownIndex = -1;

        for (let i = 0; (ownIndex === -1 || !needsNthChild) && i < siblings.length; ++i) {
            const sibling = siblings[i];

            if (sibling === node) {
                ownIndex = i;
                continue;
            }

            if (needsNthChild) {
                continue;
            }

            if (sibling?.nodeName.toLowerCase() !== nodeName.toLowerCase()) {
                continue;
            }

            needsClassNames = true;
            const ownClassNames = prefixedOwnClassNamesArray;
            let ownClassNameCount = 0;

            for (const _ in ownClassNames) {
                void _;
                ++ownClassNameCount;
            }

            if (ownClassNameCount === 0) {
                needsNthChild = true;
                continue;
            }

            const siblingClassNamesArray = this.prefixedElementClassNames(sibling);

            for (const siblingClass of siblingClassNamesArray) {
                if (ownClassNames.indexOf(siblingClass)) {
                    continue;
                }

                // TODO: check the logic of this
                delete (ownClassNames as any)[siblingClass];

                if (!--ownClassNameCount) {
                    needsNthChild = true;
                    break;
                }
            }
        }

        let result = nodeName.toLowerCase();

        if (
            isTargetNode &&
            nodeName.toLowerCase() === 'input' &&
            node.getAttribute('type') &&
            !node.getAttribute('id') &&
            !node.getAttribute('class')
        ) {
            result += '[type="' + node.getAttribute('type') + '"]';
        }
        if (needsNthChild) {
            result += ':nth-child(' + (ownIndex + 1) + ')';
        } else if (needsClassNames) {
            // for (var prefixedName in prefixedOwnClassNamesArray.keySet())
            for (const prefixedName in prefixedOwnClassNamesArray) {
                result +=
                    '.' +
                    this.escapeIdentifierIfNeeded(
                        prefixedOwnClassNamesArray[prefixedName]?.substr(1) || '',
                    );
            }
        }

        return new CSSPathStep(result, false);
    }

    private prefixedElementClassNames(node: Element): string[] {
        const classAttribute = node.getAttribute('class');

        if (!classAttribute) {
            return [];
        }

        return classAttribute
            .split(/\s+/g)
            .filter(Boolean)
            .map(
                (name: string) =>
                    // The prefix is required to store "__proto__" in a object-based map.
                    '$' + name,
            );
    }

    private idSelector(id: string): string {
        return '#' + this.escapeIdentifierIfNeeded(id);
    }

    private escapeIdentifierIfNeeded(ident: string): string {
        if (this.isCSSIdentifier(ident)) {
            return ident;
        }
        const shouldEscapeFirst = /^(?:[0-9]|-[0-9-]?)/.test(ident);
        const lastIndex = ident.length - 1;

        return ident.replace(/./g, (c, i) =>
            (shouldEscapeFirst && i === 0) || !this.isCSSIdentChar(c)
                ? this.escapeAsciiChar(c, i === lastIndex)
                : c,
        );
    }

    private escapeAsciiChar(c: string, isLast: boolean): string {
        return '\\' + this.toHexByte(c) + (isLast ? '' : ' ');
    }

    private toHexByte(c: string): string {
        let hexByte = c.charCodeAt(0).toString(16);

        if (hexByte.length === 1) {
            hexByte = '0' + hexByte;
        }

        return hexByte;
    }

    private isCSSIdentChar(c: string): boolean {
        if (/[a-zA-Z0-9_-]/.test(c)) {
            return true;
        }

        return c.charCodeAt(0) >= 0xa0;
    }

    private isCSSIdentifier(value: string): boolean {
        return /^-?[a-zA-Z_][a-zA-Z0-9_-]*$/.test(value);
    }
}
